/**
 * App — root routing.
 *
 * Strategy:
 *  - Public marketing + auth routes (Landing, Login, Signup, ForgotPassword,
 *    Legal) are eagerly imported so first paint is fast for visitors.
 *  - Authenticated app surfaces are React.lazy + Suspense — Vite's manualChunks
 *    config splits Radix / Recharts / Firebase / Sonner into their own
 *    long-cached chunks, so navigating between admin pages is near-instant
 *    after the first load.
 *  - <RouteTransition> wraps every route with a 180ms Apple-quiet fade.
 *  - <Toaster /> from sonner is the modern toast surface; the legacy useToast
 *    pattern still works for backwards compat until Phase 4.
 */

import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';

import { ToastContainer, useToast } from './components/Toast';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import { RouteFallback } from './components/RouteFallback';
import { RouteTransition } from './components/RouteTransition';

// Eager — small public surface that should be on the critical path.
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import { Terms, Privacy } from './pages/Legal';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy — workspace pages. Each becomes its own JS chunk.
const Dashboard = React.lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })),
);
const History = React.lazy(() => import('./pages/History'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const Providers = React.lazy(() => import('./pages/Providers'));
const ApiKeys = React.lazy(() => import('./pages/ApiKeys'));
const Team = React.lazy(() => import('./pages/Team'));
const Budgets = React.lazy(() => import('./pages/Budgets'));
const AuditLogs = React.lazy(() => import('./pages/AuditLogs'));
const Settings = React.lazy(() => import('./pages/Settings'));
const Alerts = React.lazy(() => import('./pages/Alerts'));
const Billing = React.lazy(() => import('./pages/Billing'));
const DemoDashboard = React.lazy(() => import('./pages/DemoDashboard'));
const Blog = React.lazy(() => import('./pages/Blog'));
const BlogPost = React.lazy(() => import('./pages/BlogPost'));

/**
 * AnimatedRoutes — Routes wrapped in AnimatePresence so route-exit transitions
 * play before the new route mounts. Must live inside <BrowserRouter> so
 * useLocation() resolves.
 */
function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <RouteTransition key={location.pathname}>
        <Routes location={location}>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/demo" element={<DemoDashboard />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Authenticated */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/history"
            element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            }
          />
          <Route
            path="/providers"
            element={
              <ProtectedRoute>
                <Providers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-keys"
            element={
              <ProtectedRoute>
                <ApiKeys />
              </ProtectedRoute>
            }
          />
          <Route
            path="/audit-logs"
            element={
              <ProtectedRoute>
                <AuditLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute>
                <Team />
              </ProtectedRoute>
            }
          />
          <Route
            path="/budgets"
            element={
              <ProtectedRoute>
                <Budgets />
              </ProtectedRoute>
            }
          />

          {/* Roadmap stubs */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/alerts"
            element={
              <ProtectedRoute>
                <Alerts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/billing"
            element={
              <ProtectedRoute>
                <Billing />
              </ProtectedRoute>
            }
          />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </RouteTransition>
    </AnimatePresence>
  );
}

const App: React.FC = () => {
  const { toasts, closeToast } = useToast();

  return (
    <BrowserRouter>
      <Suspense fallback={<RouteFallback />}>
        <AnimatedRoutes />
      </Suspense>

      {/* Modern toast surface (theme-aware). */}
      <SonnerToaster position="top-right" />

      {/* Legacy toast surface — preserved during the migration. */}
      <ToastContainer toasts={toasts} onClose={closeToast} />
    </BrowserRouter>
  );
};

export default App;
